import { useState, useCallback, useEffect } from 'react';
import { api } from '@/utils/api';

export interface RecentItem {
  id: string;
  label: string;
  type: string;
  timestamp: number;
  href?: string;
  isPinned?: boolean;
  [key: string]: any;
}

interface ActivityLogEntry {
  id: string;
  report_id: string;
  action_type: string;
  action_details?: string;
  section?: string;
  timestamp: string;
  client_name?: string;
  business_name?: string;
}

const STORAGE_KEY = 'claimgenius:recents';
const PINNED_KEY = 'claimgenius:pinned';
const MAX_RECENTS = 10;
const MAX_PINNED = 5;

/**
 * useRecents — LocalStorage ring buffer + activity-log API hydration
 *
 * Hydrates recents from the activity-log API (/api/v1/activity/user/me)
 * and maintains a localStorage ring buffer for quick access.
 * Also manages pinned items separately.
 */
export function useRecents() {
  const [recents, setRecents] = useState<RecentItem[]>([]);
  const [pinned, setPinned] = useState<RecentItem[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHydrating, setIsHydrating] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    const storedRecents = localStorage.getItem(STORAGE_KEY);
    const storedPinned = localStorage.getItem(PINNED_KEY);

    if (storedRecents) {
      try {
        setRecents(JSON.parse(storedRecents));
      } catch {
        setRecents([]);
      }
    }

    if (storedPinned) {
      try {
        setPinned(JSON.parse(storedPinned));
      } catch {
        setPinned([]);
      }
    }

    setIsLoaded(true);
  }, []);

  // Hydrate from activity-log API
  const hydrateFromApi = useCallback(async () => {
    setIsHydrating(true);
    try {
      const activities = await api.get<ActivityLogEntry[]>('/api/v1/activity/user/me?limit=20', {
        showErrorToast: false,
      });

      if (activities && Array.isArray(activities)) {
        // Transform activity log entries to RecentItem format
        const apiRecents: RecentItem[] = activities
          .filter((activity) => activity.report_id && activity.report_id !== 'system')
          .slice(0, MAX_RECENTS)
          .map((activity) => ({
            id: activity.id,
            label: sanitizeActionDetails(activity.action_details, activity)
              || `${activity.action_type} — ${activity.client_name || activity.business_name || activity.report_id.slice(0, 8)}`,
            type: mapActionTypeToRecentType(activity.action_type),
            timestamp: new Date(activity.timestamp).getTime(),
            href: `/case/${activity.report_id}`,
            reportId: activity.report_id,
          }));

        // Merge with existing recents, preferring newer API data
        setRecents((prev) => {
          const merged = mergeRecents(prev, apiRecents);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
          return merged;
        });
      }
    } catch (e) {
      // Silently fail — localStorage recents are sufficient fallback
      console.warn('Failed to hydrate recents from API:', e);
    } finally {
      setIsHydrating(false);
    }
  }, []);

  const add = useCallback(
    (item: Omit<RecentItem, 'timestamp'>) => {
      setRecents((prev) => {
        const filtered = prev.filter((r) => r.id !== item.id);
        const newItem: RecentItem = { ...item, timestamp: Date.now() } as RecentItem;
        const updated = [newItem, ...filtered].slice(0, MAX_RECENTS);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
    },
    []
  );

  const remove = useCallback((id: string) => {
    setRecents((prev) => {
      const updated = prev.filter((r) => r.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const clear = useCallback(() => {
    setRecents([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const pin = useCallback((item: RecentItem) => {
    setPinned((prev) => {
      const filtered = prev.filter((p) => p.id !== item.id);
      const updated = [{ ...item, isPinned: true }, ...filtered].slice(0, MAX_PINNED);
      localStorage.setItem(PINNED_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const unpin = useCallback((id: string) => {
    setPinned((prev) => {
      const updated = prev.filter((p) => p.id !== id);
      localStorage.setItem(PINNED_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const sorted = recents.slice().sort((a, b) => b.timestamp - a.timestamp);

  return {
    recents: sorted,
    pinned,
    add,
    remove,
    clear,
    pin,
    unpin,
    isLoaded,
    isHydrating,
    hydrateFromApi,
  };
}

/**
 * Activity rows are written by whichever surface performed the action, and
 * some historical writers interpolated a name that was undefined on canonical
 * drafts — the attorney Recents rail literally listed "Generated Future
 * Closure projection for undefined". Those rows are already in the log, so
 * repair them at render: substitute the entry's own client/business name when
 * one is available, otherwise drop the dangling "for undefined" tail rather
 * than print it. Returns '' when the string is unusable, so the caller's
 * fallback label takes over.
 */
export function sanitizeActionDetails(
  details: string | undefined | null,
  activity: { client_name?: string; business_name?: string }
): string {
  if (!details) return '';
  if (!/\b(undefined|null|NaN)\b/.test(details)) return details;

  const real = activity.client_name || activity.business_name;
  if (real) {
    return details.replace(/\b(undefined|null|NaN)\b/g, real);
  }
  // No name to substitute — strip the trailing "for undefined" clause.
  const trimmed = details
    .replace(/\s+(for|to)\s+(undefined|null|NaN)\b\s*/gi, '')
    .replace(/\b(undefined|null|NaN)\b/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return trimmed;
}

/**
 * Map activity log action_type to RecentItem type
 */
function mapActionTypeToRecentType(actionType: string): string {
  const typeMap: Record<string, string> = {
    view: 'case',
    edit: 'case',
    generate: 'document',
    download: 'document',
    share: 'case',
    comment: 'case',
    delete: 'case',
    duplicate: 'case',
  };
  return typeMap[actionType] || 'tool';
}

/**
 * Merge two arrays of recents, keeping the most recent items
 */
function mergeRecents(local: RecentItem[], api: RecentItem[]): RecentItem[] {
  const merged = new Map<string, RecentItem>();

  // Add local items first
  local.forEach((item) => merged.set(item.id, item));

  // Add/overwrite with API items (they're newer)
  api.forEach((item) => merged.set(item.id, item));

  // Convert to array, sort by timestamp desc, and limit
  return Array.from(merged.values())
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, MAX_RECENTS);
}

export default useRecents;
